import React, { useCallback, useMemo } from 'react';

import { useSignOutMutation } from 'services/user-service/userService';
import { clearUser, setUserIsLoading } from 'slices/userSlice';
import { useAppDispatch } from 'store';

import Typography from 'components/typography';

import { WATCHBLOCK_REFRESH_TOKEN, WATCHBLOCK_TOKEN } from 'constants/token';
import { SetStateTypes } from 'types/setStateTypes';

import { ReactComponent as Logout } from 'assets/icons/logout-icon.svg';
import { ReactComponent as Settings } from 'assets/icons/settings-icon.svg';

import s from './Email.module.scss';

const SETTINGS_KEY = 'SETTINGS_KEY';
const SIGN_OUT_KEY = 'SIGN_OUT_KEY';

interface useEmailDropdownItemsProps {
  setIsOverlayOpened: SetStateTypes<boolean>;
  setIsModalOpened: SetStateTypes<boolean>;
}

const useEmailDropdownItems = (props: useEmailDropdownItemsProps) => {
  const { setIsOverlayOpened, setIsModalOpened } = props;

  const dispatch = useAppDispatch();

  const [signOut] = useSignOutMutation();

  const signOutHandler = useCallback(() => {
    dispatch(setUserIsLoading(true));
    signOut()
      .then(() => {
        localStorage.removeItem(WATCHBLOCK_REFRESH_TOKEN);
        localStorage.removeItem(WATCHBLOCK_TOKEN);
        dispatch(clearUser());
      })
      .finally(() => {
        dispatch(setUserIsLoading(false));
      });
  }, [dispatch, signOut]);

  const openModalHandler = useCallback(() => {
    setIsOverlayOpened(false);
    setIsModalOpened(true);
  }, [setIsModalOpened, setIsOverlayOpened]);

  const items = useMemo(() => {
    return [
      {
        label: (
          <div className={s.option}>
            <Settings />
            <Typography>Settings</Typography>
          </div>
        ),
        key: SETTINGS_KEY,
      },
      {
        label: (
          <div className={s.option}>
            <Logout />
            <Typography>Log out</Typography>
          </div>
        ),
        key: SIGN_OUT_KEY,
      },
    ];
  }, []);

  const handleItemClick = useCallback(
    ({ key }: { key: string }) => {
      switch (key) {
        case SETTINGS_KEY:
          return openModalHandler();
        case SIGN_OUT_KEY:
          return signOutHandler();
        default:
          return;
      }
    },
    [openModalHandler, signOutHandler],
  );

  return {
    items,
    handleItemClick,
  };
};

export default useEmailDropdownItems;
